import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import awsConfig from "../aws-config";
import { resolveAuthToken } from "../utils/authToken";
import LogoImg from "../assets/layerbound-white.png";
import "./RecruitmentOnboarding.css";

const API_URL = awsConfig.api_url_create_business;
const PRESIGN_API = awsConfig.api_url_presign_logo;
const MAX_LOGO_SIZE = 200 * 1024;

async function uploadLogoToS3(file, businessId, authToken) {
  const res = await fetch(
    `${PRESIGN_API}?businessId=${businessId}&fileName=${encodeURIComponent(file.name)}&contentType=${encodeURIComponent(file.type)}`,
    { method: "GET", headers: { Authorization: `Bearer ${authToken}` } }
  );
  if (!res.ok) throw new Error("Failed to get presigned URL");
  const { uploadUrl, publicUrl } = await res.json();
  if (!uploadUrl) throw new Error("Invalid presign response");

  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!putRes.ok) throw new Error("Failed to upload logo");
  return publicUrl;
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function RecruitmentOnboarding({ onComplete }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogoChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_SIZE) {
      setError("Logo must be under 200 KB.");
      return;
    }
    setError("");
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target.result || "");
    reader.readAsDataURL(file);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      setError("Enter your company or agency name.");
      return;
    }
    if (!isValidEmail(email)) {
      setError("Enter a valid work email.");
      return;
    }

    setLoading(true);
    setError("");
    const businessId = uuidv4();

    try {
      const user = await getCurrentUser().catch(() => null);
      if (!user) {
        navigate("/auth");
        return;
      }
      const { tokens } = await fetchAuthSession();
      const authToken = resolveAuthToken(tokens);
      if (!authToken) {
        navigate("/auth");
        return;
      }

      let logoUrl = "";
      if (logoFile) {
        logoUrl = await uploadLogoToS3(logoFile, businessId, authToken);
      }

      const payload = new FormData();
      payload.append("businessId", businessId);
      payload.append("name", name.trim());
      payload.append("email", email.trim());
      payload.append("phone", phone.trim());
      payload.append("logoUrl", logoUrl);
      payload.append("stripeConnected", "false");
      payload.append("openingHours", JSON.stringify({}));
      payload.append("addressCity", location.trim());
      payload.append("addressCountry", "United Kingdom");
      payload.append("onboardingComplete", "true");

      const res = await fetch(API_URL, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
        body: payload,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Failed to create account.");
      }

      const data = await res.json();
      onComplete?.({ ...data, businessId });
      navigate("/recruiter", { replace: true });
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="recruitment-onboarding">
      <header className="recruitment-onboarding__header">
        <img src={LogoImg} alt="Layerbound" className="recruitment-onboarding__logo" />
        <div>
          <p className="recruitment-onboarding__eyebrow">Get started</p>
          <h1>Set up your intake links</h1>
          <p className="recruitment-onboarding__subtitle">
            Share a branded link in minutes. You can update details later.
          </p>
        </div>
      </header>

      <form className="recruitment-onboarding__card" onSubmit={handleSubmit}>
        <label className="recruitment-onboarding__field">
          <span>Organisation name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Birmingham Electrical Training"
            required
          />
        </label>
        <label className="recruitment-onboarding__field">
          <span>Work email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            required
          />
        </label>
        <label className="recruitment-onboarding__field">
          <span>Phone (optional)</span>
          <input
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+44 7700 900000"
          />
        </label>
        <label className="recruitment-onboarding__field">
          <span>Location (optional)</span>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Milton Keynes, UK"
          />
        </label>
        <div className="recruitment-onboarding__field recruitment-onboarding__field--logo">
          <span>Logo (optional)</span>
          <div className="recruitment-onboarding__logo-row">
            <button
              type="button"
              className="recruitment-onboarding__logo-button"
              onClick={() => fileInputRef.current?.click()}
            >
              Upload logo
            </button>
            {logoPreview && (
              <div className="recruitment-onboarding__logo-preview">
                <img src={logoPreview} alt="Logo preview" />
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
            className="recruitment-onboarding__file-input"
          />
        </div>

        {error && <div className="recruitment-onboarding__error">{error}</div>}

        <button
          type="submit"
          className="recruitment-onboarding__submit"
          disabled={loading}
        >
          {loading ? "Creating…" : "Create intake link"}
        </button>
      </form>
    </div>
  );
}

export default RecruitmentOnboarding;
